import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { formatDate } from "@sp-crm/core";
import {
    WorkflowActionChip,
    workflowActionChips,
    WorkflowChipSource,
} from "components/manage/manage-workflows/builder/builder-helper";
import { ActionChip } from "components/manage/manage-workflows/builder/sequence-strip";
import { DeleteButton } from "components/ui/action-button";
import { Badge, BadgeProps } from "components/ui/badge";
import {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
} from "lib/headlessui/components/disclosure/disclosure";
import React from "react";
import {
    WorkflowRunSequence,
    WorkflowRunSequenceExecution,
    WorkflowRunSequenceRow,
    WorkflowRunSequenceState,
} from "./workflow-run-sequences";
import {
    mutedTitleClassName,
    ResultDetail,
    runDisplayState,
    RunStatusIcon,
    runStatusText,
    runStatusTextClassName,
    runTitleClassName,
    stopWorkflowConfirm,
    WorkflowRunState,
} from "./workflow-run-status";

export type SequencedWorkflow = WorkflowRunSequenceExecution["workflow"] &
    WorkflowChipSource & { name: string };

export type SequencedExecution = WorkflowRunSequenceExecution &
    WorkflowRunState & { workflow: SequencedWorkflow };

export type SequenceDisclosure = "open" | "collapsed";

interface WorkflowRunSequenceCardProps {
    sequence: WorkflowRunSequence<SequencedExecution>;
    disclosure: SequenceDisclosure;
    onCancel: (execution: SequencedExecution) => Promise<void>;
}

interface SequenceBadge {
    label: string;
    type: BadgeProps["type"];
}

const stateBadge: Record<WorkflowRunSequenceState, SequenceBadge> = {
    inProgress: { label: "In progress", type: "info" },
    completed: { label: "Completed", type: "success" },
    completedWithWarnings: { label: "Completed with warnings", type: "warning" },
    stopped: { label: "Stopped", type: "error" },
    cancelled: { label: "Cancelled", type: "info" },
};

const actionsRun = (sequence: WorkflowRunSequence<SequencedExecution>): number =>
    sequence.rows.filter(row => row.kind === "executed" && !!row.execution.finishedAt)
        .length;

interface ExecutedRowProps {
    execution: SequencedExecution;
    position: number | null;
    workflowName: string;
    chip: WorkflowActionChip | null;
    onCancel: (execution: SequencedExecution) => Promise<void>;
}

const ExecutedRow: React.FC<ExecutedRowProps> = props => {
    const { execution, position, workflowName, chip, onCancel } = props;
    const state = runDisplayState(execution);
    return (
        <div className="py-2 space-y-1">
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
                <div className="flex flex-wrap items-center gap-2">
                    <RunStatusIcon state={state} />
                    <span className={runTitleClassName(state)}>
                        {position === null ? workflowName : `Action ${position + 1}`}
                    </span>
                    {chip ? <ActionChip chip={chip} /> : null}
                </div>
                <div className="flex items-center gap-2 pl-7 sm:pl-0">
                    <span className={runStatusTextClassName}>
                        {runStatusText(execution)}
                    </span>
                    {state === "scheduled" ? (
                        <DeleteButton
                            confirm={stopWorkflowConfirm}
                            onClick={() => onCancel(execution)}
                            backgroundColor="bg-gray-200"
                        />
                    ) : null}
                </div>
            </div>
            <ResultDetail detail={execution.resultDetail} className="pl-7" />
        </div>
    );
};

interface NotRunRowProps {
    position: number;
    chip: WorkflowActionChip | null;
}

const NotRunRow: React.FC<NotRunRowProps> = props => {
    const { position, chip } = props;
    return (
        <div className="flex flex-wrap items-center gap-2 py-2">
            <span className="h-5 w-5 shrink-0 rounded-full border-2 border-gray-200" />
            <span className={mutedTitleClassName}>Action {position + 1}</span>
            {chip ? <ActionChip chip={chip} /> : null}
        </div>
    );
};

interface SequenceRowProps {
    row: WorkflowRunSequenceRow<SequencedExecution>;
    workflowName: string;
    chips: WorkflowActionChip[];
    onCancel: (execution: SequencedExecution) => Promise<void>;
}

const chipAt = (
    chips: WorkflowActionChip[],
    position: number | null,
): WorkflowActionChip | null => (position === null ? null : (chips[position] ?? null));

const SequenceRow: React.FC<SequenceRowProps> = props => {
    const { row, workflowName, chips, onCancel } = props;
    switch (row.kind) {
        case "executed":
            return (
                <ExecutedRow
                    execution={row.execution}
                    position={row.position}
                    workflowName={workflowName}
                    chip={chipAt(chips, row.position)}
                    onCancel={onCancel}
                />
            );
        case "notRun":
            return (
                <NotRunRow position={row.position} chip={chipAt(chips, row.position)} />
            );
        default: {
            const exhaustiveCheck: never = row;
            throw new Error(`Unhandled row: ${JSON.stringify(exhaustiveCheck)}`);
        }
    }
};

export const WorkflowRunSequenceCard: React.FC<WorkflowRunSequenceCardProps> = props => {
    const { sequence, disclosure, onCancel } = props;
    const { workflow } = sequence;
    const chips = workflowActionChips(workflow);
    const badge = stateBadge[sequence.state];
    return (
        <Disclosure defaultOpen={disclosure === "open"}>
            {({ open }) => (
                <div className="rounded-md border border-gray-200">
                    <DisclosureButton className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 text-left hover:bg-gray-50">
                        <ChevronDownIcon
                            className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
                                open ? "rotate-0" : "-rotate-90"
                            }`}
                            aria-hidden="true"
                        />
                        <span className="text-sm font-medium text-gray-900">
                            {workflow.name}
                        </span>
                        <Badge value={badge.label} type={badge.type} />
                        <span className="text-xs text-gray-500">
                            Started {formatDate(sequence.startedAt)} &middot;{" "}
                            {actionsRun(sequence)} of {workflow.actions.length} actions
                            run
                        </span>
                    </DisclosureButton>
                    <DisclosurePanel className="divide-y divide-gray-100 border-t border-gray-200 px-3">
                        {sequence.rows.map(row => (
                            <SequenceRow
                                key={row.key}
                                row={row}
                                workflowName={workflow.name}
                                chips={chips}
                                onCancel={onCancel}
                            />
                        ))}
                    </DisclosurePanel>
                </div>
            )}
        </Disclosure>
    );
};
